import { useMemo, useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import ToggleGroup from "@/v2-ui/toggleGroup"
import Manage from "@/v2-backoffice-client/components/manage"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"
import LicenseCoverageCard from "@/v2-backoffice-client/license/coverage/LicenseCoverageCard"
import { useLicenseCoverageQuery } from "@/v2-backoffice-client/license/coverage/license.coverage.query"
import {
  LICENSE_COVERAGE_FILTER_OPTIONS,
  type LicenseCoverageFilter,
} from "@/v2-backoffice-client/license/coverage/license.coverage.constants"
import {
  filterCoverageRows,
  getLicenseCoverageWindow,
  summarizeCoverage,
  type LicenseCoverageRow,
} from "@/v2-backoffice-client/license/coverage/license.coverage.utils"

export type LicenseCoverageBoardProps = ComponentProps<"section">

const EMPTY_ROWS: LicenseCoverageRow[] = []

function isCoverageFilter(value: string): value is LicenseCoverageFilter {
  return LICENSE_COVERAGE_FILTER_OPTIONS.some((option) => option.value === value)
}

// Licensing → Coverage: every license on a timeline, the ones where
// billing, invoicing and access disagree on top, each with the fix beside
// it. The board automates nothing itself — the hourly sync does — it is
// where the automation's blind spots become visible: a subscription keyed
// in a year late, an invoice paid for a period nothing covers, a trial
// that ran out while people keep knocking.
function LicenseCoverageBoard(props: LicenseCoverageBoardProps) {
  const { className, ...sectionProps } = props
  const [filter, setFilter] = useState<LicenseCoverageFilter>("issues")
  const [search, setSearch] = useState("")

  const { data, previousData, loading, error } = useLicenseCoverageQuery()
  const rows = (data ?? previousData)?.getLicenseCoverage ?? EMPTY_ROWS
  const window = useMemo(() => getLicenseCoverageWindow(), [])
  const summary = useMemo(() => summarizeCoverage(rows), [rows])

  const visible = useMemo(() => {
    const filtered = filterCoverageRows(rows, filter)
    const needle = search.trim().toLowerCase()
    if (!needle) return filtered
    return filtered.filter(
      (row) =>
        row.CustomerName.toLowerCase().includes(needle) ||
        row.ProductName.toLowerCase().includes(needle) ||
        (row.PlanName ?? "").toLowerCase().includes(needle),
    )
  }, [rows, filter, search])

  const counts = useMemo(
    () => ({
      issues: filterCoverageRows(rows, "issues").length,
      critical: filterCoverageRows(rows, "critical").length,
      all: rows.length,
    }),
    [rows],
  )

  return (
    <section
      className={cn("flex flex-col gap-4", className)}
      data-feature-code="UI_BackofficeLicenseCoverage_Board"
      {...sectionProps}
    >
      <div className={usageKpiGridClass(4)}>
        <UsageReportKpi
          label="Critical"
          value={formatNumber(summary.critical)}
          sub="licenses with a hole nobody is let in through"
          tone={summary.critical > 0 ? "warning" : "default"}
        />
        <UsageReportKpi
          label="Warnings"
          value={formatNumber(summary.warning)}
          sub="bridged holes, overdue starts, trials that ran out"
        />
        <UsageReportKpi
          label="Knocking"
          value={formatNumber(summary.knockingCustomers)}
          sub={`customers with an issue and ${formatNumber(summary.attempts7d)} refused attempts this week`}
          tone={summary.knockingCustomers > 0 ? "warning" : "default"}
        />
        <UsageReportKpi
          label="Licenses"
          value={formatNumber(summary.licenses)}
          sub="tracked, every customer and product"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ToggleGroup
          type="single"
          size="xs"
          variant="outline"
          value={filter}
          aria-label="Which licenses to show"
          onValueChange={(next) => {
            if (isCoverageFilter(next)) setFilter(next)
          }}
        >
          {LICENSE_COVERAGE_FILTER_OPTIONS.map((option) => (
            <ToggleGroup.Item
              key={option.value}
              value={option.value}
              className="gap-1.5"
              data-feature-code="UI_BackofficeLicenseCoverage_Filter"
            >
              {option.label}
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {counts[option.value]}
              </span>
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
        <Input
          type="search"
          value={search}
          placeholder="Search customer, product or license…"
          className="h-8 w-72 text-sm"
          onChange={(event) => {
            setSearch(event.target.value)
          }}
        />
        <Legend className="ml-auto" />
      </div>

      <ErrorMessage error={error} />

      {loading && rows.length === 0 ? (
        <Manage.Skeleton count={3} aria-busy="true" />
      ) : visible.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground">
          {filter === "all"
            ? "No licenses match."
            : "Nothing to fix: every license's billing, invoicing and access agree today."}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map((coverage, index) => (
            <LicenseCoverageCard
              key={coverage.LicenseId}
              coverage={coverage}
              window={window}
              showAxis={index === 0}
            />
          ))}
        </div>
      )}
    </section>
  )
}

type LegendProps = { className?: string }

// What the bars mean, once, in the words the pills use.
function Legend(props: LegendProps) {
  const { className } = props
  return (
    <ul
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground",
        className,
      )}
      aria-label="Timeline legend"
    >
      <LegendItem swatch="bg-green-500/80" label="Active subscription" />
      <LegendItem
        swatch="border border-dashed border-muted-foreground/70 bg-muted-foreground/10"
        label="Scheduled"
      />
      <LegendItem swatch="bg-muted-foreground/25" label="Ended" />
      <LegendItem swatch="bg-amber-500/70" label="Operator trial" />
      <LegendItem swatch="bg-foreground/35" label="Invoiced (paid)" />
      <LegendItem swatch="bg-amber-500/60" label="Invoiced (open)" />
      <LegendItem swatch="border-x border-red-500/70 bg-red-500/15" label="Hole" />
    </ul>
  )
}

function LegendItem(props: { swatch: string; label: string }) {
  const { swatch, label } = props
  return (
    <li className="flex items-center gap-1.5">
      <span className={cn("inline-block h-2.5 w-4 rounded-sm", swatch)} />
      {label}
    </li>
  )
}

export default LicenseCoverageBoard
